import React, { useMemo } from 'react';
import { Box, Typography, Divider, Tooltip, tooltipClasses } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

import EventTimelineBarChart from 'components/EventTimelineBarChart';

import icn_linebar_chart from 'assets/images/icons/icn_linebar_chart.png';
import { ReactComponent as Clock } from 'assets/images/icons/icn_display_clock.svg';
import icn_info_solid from 'assets/images/icons/icn_Info_solid.svg';
import CircularLoader from 'components/CircularLoader';

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#4D4D4D',
    color: '#FFFFFF',
    maxWidth: 480,
    fontSize: theme.typography.pxToRem(12),
    padding: '16px 20px',
    borderRadius: '8px',
    lineHeight: '1.5',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.3)',
  },
}));

const TooltipContent = () => (
  <Box>
    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Good — No Unexpected Reboots</Typography>
      <Typography variant="body2" sx={{ fontSize: '12px' }}>
        Devices are stable with no unexpected restarts detected.
      </Typography>
    </Box>

    <Box sx={{ mb: 2.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Warning — &gt; 0% and &lt; 2% of Devices Experienced Unexpected Reboot</Typography>
      <Typography variant="body2" sx={{ fontSize: '12px', mb: 1 }}>
        A few devices rebooted unexpectedly — likely isolated power fluctuations or hardware triggers.
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Recommended Actions:</Typography>
      <Box component="ul" sx={{ pl: 2, m: 0, fontSize: '12px' }}>
        <li>Check device logs, power connections, and operating temperature.</li>
      </Box>
    </Box>

    <Box>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Critical — ≥ 2% of Devices Experienced Unexpected Reboot</Typography>
      <Typography variant="body2" sx={{ fontSize: '12px', mb: 1 }}>
        A significant share of devices rebooted unexpectedly, risking cascading outages and downtime.
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>Recommended Actions:</Typography>
      <Box component="ul" sx={{ pl: 2, m: 0, fontSize: '12px' }}>
        <li>Pull crash logs to determine whether the cause is software or hardware.</li>
        <li>Verify firmware versions and PoE budget; replace persistently failing hardware.</li>
      </Box>
    </Box>
  </Box>
);

const DeviceRebootEvents = ({ data, loading, isEmpty, onClick }) => {
  const theme = useTheme();
  const handleOpenDrawer = !isEmpty ? onClick : undefined;

  const getStatus = (reboot, all) => {
    if (reboot === 0) return { label: 'Good', color: '#4CAF50' };
    const ratio = reboot / all;
    if (ratio < 0.02) return { label: 'Warning', color: '#FF9800' };
    return { label: 'Critical', color: '#F44336' };
  };

  const status = data ? getStatus(data.rebootCount, data.allOnlineDevice) : { label: 'Good', color: '#4CAF50' };
  const totalReboots = data ? data.rebootCount : 0;
  const peakHourCount = data && data.series ? Math.max(...data.series.map(d => d[1]), 0) : 0;

  // Prepare data for PeriodicBarChart
  const barChartData = useMemo(() => {
    if (!data) return null;
    const now = new Date();
    const startHourTs = new Date(now).setMinutes(0, 0, 0, 0) - 23 * 60 * 60 * 1000;

    return {
      series: [
        {
          name: 'Reboots',
          data: data.series
            .map(d => {
              // 確保將 UTC 字串正確轉為 Local Timestamp
              const ts = typeof d[0] === 'string' && !d[0].endsWith('Z') && !d[0].includes('+')
                ? new Date(d[0] + 'Z').getTime()
                : new Date(d[0]).getTime();
              return [ts, d[1]];
            })
            .filter(d => d[0] >= startHourTs)
        }
      ]
    };
  }, [data]);


  return (
    <Box
      sx={{
        border: '1px solid #E2E8F0',
        borderRadius: '12px',
        p: 2,
        bgcolor: 'white',
        mb: 3,
        cursor: isEmpty ? 'default' : 'pointer',
        '&:hover': handleOpenDrawer ? {
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
        } : {}
      }}
      onClick={handleOpenDrawer}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            component="img"
            src={icn_linebar_chart}
            sx={{ width: '18px', height: '18px', mr: 1 }}
          />
          <Typography sx={{ fontWeight: 'bold', color: '#172664', fontSize: '15px', mr: 1 }}>
            Unexpected Reboot
          </Typography>
          <HtmlTooltip title={<TooltipContent />} placement="auto">
            <Box
              component="img"
              src={icn_info_solid}
              sx={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
          </HtmlTooltip>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            color: theme.palette.primary.main,
          }}
        >
          <Clock />
          <Typography
            sx={{
              marginLeft: '5px',
              fontSize: theme.typography.customFontSize[2],
              fontWeight: theme.typography.customFontWeight[2],
            }}
          >
            Last 24 Hours
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularLoader />
        </Box>
      ) : isEmpty ? (
        <Box sx={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ color: theme.palette.primary.greyThird, fontSize: theme.typography.customFontSize[2] }}>
            No data available
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
          <Box sx={{ width: { xs: '100%', md: '25%' }, pr: { xs: 0, md: 4 }, mb: { xs: 3, md: 0 } }}>
            <Typography sx={{ color: '#1C1C1E', fontWeight: 'bold', fontSize: '13px', mb: 1 }}>Unexpected Reboot Status</Typography>
            <Typography sx={{ fontSize: '35px', fontWeight: 'bold', color: status.color, lineHeight: 1.2, mb: 2 }}>
              {status.label === 'Warning' ? 'Warn' : status.label}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#172664' }}>Total Reboots:</Typography>
                <Typography sx={{ fontSize: '13px', color: '#4C6EF5', fontWeight: 'bold' }}>{totalReboots}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Typography sx={{ fontSize: '13px', fontWeight: 'bold', color: '#172664' }}>Peak Reboot Count per Hour:</Typography>
                <Typography sx={{ fontSize: '13px', color: '#4C6EF5', fontWeight: 'bold' }}>{peakHourCount}</Typography>
              </Box>
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, mr: 4, borderColor: '#EDF2F7' }} />

          <Box sx={{ flex: 1, width: '100%' }}>
            <EventTimelineBarChart barChartData={barChartData} height="200px" />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default DeviceRebootEvents;